/**
 * InvoiceTemplate Component
 * Professional invoice layout for printing and display
 */

import { Printer } from 'lucide-react';
import type { Invoice, LineItem } from '../../types/payment';
import { CurrencyDisplay } from './CurrencyDisplay';
import { PaymentStatusBadge } from './PaymentStatusBadge';

export interface InvoiceTemplateProps {
  invoice: Invoice;
  companyInfo?: {
    name: string;
    address: string;
    phone?: string;
    email?: string;
    taxId?: string;
    logo?: string;
  };
  onPrint?: () => void;
  onDownload?: () => void;
  className?: string;
}

export function InvoiceTemplate({
  invoice,
  companyInfo,
  onPrint,
  onDownload,
  className = '',
}: InvoiceTemplateProps) {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg ${className}`}>
      {/* Action Buttons (non-printable) */}
      <div className="flex justify-end gap-2 p-4 border-b border-gray-200 print:hidden">
        {onPrint && (
          <button
            onClick={onPrint}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        )}
        {onDownload && (
          <button
            onClick={onDownload}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            📥 Download PDF
          </button>
        )}
      </div>

      {/* Invoice Content */}
      <div className="p-8 sm:p-12">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            {companyInfo?.logo ? (
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                className="h-12 mb-4"
              />
            ) : (
              <div className="h-12 w-12 bg-blue-600 rounded flex items-center justify-center mb-4">
                <span className="text-white font-bold text-2xl">H</span>
              </div>
            )}
            <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
          </div>
          <div className="text-right">
            <PaymentStatusBadge status={invoice.status} size="lg" />
            <div className="mt-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Invoice #:</span> {invoice.invoiceNumber}
              </div>
              <div>
                <span className="font-medium">Date:</span> {formatDate(invoice.issueDate)}
              </div>
              <div>
                <span className="font-medium">Due Date:</span> {formatDate(invoice.dueDate)}
              </div>
            </div>
          </div>
        </div>

        {/* Company & Client Info */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          {/* From */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-2">From</h2>
            <div className="text-sm">
              <div className="font-semibold text-gray-900">{companyInfo?.name || 'HALMAS'}</div>
              {companyInfo?.address && <div className="text-gray-600 mt-1">{companyInfo.address}</div>}
              {companyInfo?.phone && <div className="text-gray-600">Phone: {companyInfo.phone}</div>}
              {companyInfo?.email && <div className="text-gray-600">Email: {companyInfo.email}</div>}
              {companyInfo?.taxId && (
                <div className="text-gray-600 mt-2">Tax ID: {companyInfo.taxId}</div>
              )}
            </div>
          </div>

          {/* To */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h2>
            <div className="text-sm">
              <div className="font-semibold text-gray-900">
                {invoice.billTo.name}
              </div>
              {invoice.billTo.address && (
                <div className="text-gray-600 mt-1">{invoice.billTo.address}</div>
              )}
              <div className="text-gray-600">Email: {invoice.billTo.email}</div>
              {invoice.billTo.phone && (
                <div className="text-gray-600">Phone: {invoice.billTo.phone}</div>
              )}
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="mb-8">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 text-sm font-semibold text-gray-700">
                  Description
                </th>
                <th className="text-right py-3 text-sm font-semibold text-gray-700">
                  Qty
                </th>
                <th className="text-right py-3 text-sm font-semibold text-gray-700">
                  Unit Price
                </th>
                <th className="text-right py-3 text-sm font-semibold text-gray-700">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item: LineItem, index: number) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-3 text-sm text-gray-900">
                    <div className="font-medium">{item.description}</div>
                  </td>
                  <td className="text-right py-3 text-sm text-gray-600">{item.quantity}</td>
                  <td className="text-right py-3 text-sm text-gray-600">
                    <CurrencyDisplay
                      amount={item.unitPrice}
                      currency={invoice.currency}
                      size="sm"
                    />
                  </td>
                  <td className="text-right py-3 text-sm text-gray-900">
                    <CurrencyDisplay
                      amount={item.amount}
                      currency={invoice.currency}
                      size="sm"
                      fontWeight="medium"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-64">
            <div className="flex justify-between py-2 text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <CurrencyDisplay
                amount={invoice.subtotal}
                currency={invoice.currency}
                size="sm"
              />
            </div>
            {invoice.taxAmount > 0 && (
              <div className="flex justify-between py-2 text-sm">
                <span className="text-gray-600">Tax:</span>
                <CurrencyDisplay
                  amount={invoice.taxAmount}
                  currency={invoice.currency}
                  size="sm"
                />
              </div>
            )}
            <div className="flex justify-between py-3 border-t-2 border-gray-300">
              <span className="font-semibold text-gray-900">Total:</span>
              <CurrencyDisplay
                amount={invoice.totalAmount}
                currency={invoice.currency}
                size="lg"
                fontWeight="bold"
              />
            </div>
            {invoice.paidDate && (
              <div className="flex justify-between py-2 text-sm text-green-600">
                <span>Status:</span>
                <span className="font-medium">Paid on {formatDate(invoice.paidDate)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="pt-8 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Thank you for your business!
          </p>
          <p className="text-xs text-gray-400 mt-2">
            This is a computer-generated invoice. No signature required.
          </p>
        </div>
      </div>
    </div>
  );
}
